import { getEnv, fetchWithToken } from '@adsk/acs-sc-web-platform-api';

import {
  BE_CONFIG,
  MAPPING_REQUEST_PATH,
  MAPPING_PERMISSION,
  getLocalStatePermissionsKey,
} from './consts';

class CostSdk {
  constructor() {
    const env = getEnv();
    this.baseUrl = BE_CONFIG[env];
    this.projectsInitialized = {};
  }

  //Wait for permission loaded from localstorage in CostProvider
  async waitPermissionLoaded(projectId, attempts = 0) {
    return new Promise((resolve) => {
      let permissions = [];
      try {
        permissions = JSON.parse(
          localStorage.getItem(getLocalStatePermissionsKey(projectId)) || '[]',
        );
      } catch (e) {
        console.error('localstorage error', e);
      }
      if (permissions?.length || attempts >= 2) {
        resolve(permissions);
      } else {
        setTimeout(async () => {
          const permissions = await this.waitPermissionLoaded(
            projectId,
            attempts + 1,
          );
          resolve(permissions);
        }, 1000);
      }
    });
  }

  async getValidPermissions({ projectId, type }) {
    let permissions = await this.waitPermissionLoaded(projectId);
    if (!permissions?.length) {
      permissions = await this.getPermissions({
        projectId,
      });
    }

    const currentPermission = permissions?.find(
      (item) => item.resourceType === MAPPING_PERMISSION[type],
    );

    if (currentPermission && currentPermission.permissionLevel === 'noAccess') {
      return [];
    } else {
      return permissions;
    }
  }

  async queryEntities({ projectId, params, type = '' }) {
    const permissions = await this.getValidPermissions({ projectId, type });
    if (!permissions?.length) {
      throw new Error('No permission');
    }

    let count = 0;

    if (!this.projectsInitialized[projectId]) {
      await new Promise((resolve, reject) => {
        const containerStatusPulling = () =>
          fetchWithToken(
            `${this.baseUrl}/v1/projects/${projectId}/status`,
          ).then((containerStatus) => {
            count++;
            if (containerStatus) {
              this.projectsInitialized[projectId] = true;
              resolve();
            } else if (count >= 10) {
              reject('Container is initializing...');
            } else {
              setTimeout(containerStatusPulling, 6000);
            }
          });

        containerStatusPulling();
      });
    }
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      const param = params[k];
      if (typeof param !== 'undefined') {
        searchParams.append(
          k,
          typeof param === 'number' ? param.toString() : param,
        );
      }
    });
    const paramsStr = searchParams.toString();

    return fetchWithToken(
      `${this.baseUrl}/v1/projects/${projectId}/${MAPPING_REQUEST_PATH[type]}${
        paramsStr ? `?${decodeURIComponent(paramsStr)}` : ''
      }`,
    );
  }

  async getEntitiesByIds({ ids, projectId, type = '' }) {
    const permissions = await this.getValidPermissions({ projectId, type });
    if (!permissions?.length) {
      return { results: [] };
    }

    return fetchWithToken(
      `${this.baseUrl}/v1/projects/${projectId}/${
        MAPPING_REQUEST_PATH[type]
      }?id=${ids.join(',')}`,
    );
  }

  async getTerminologies({ projectId }) {
    const terminologies = await fetchWithToken(
      `${this.baseUrl}/v1/projects/${projectId}/terminologies`,
    );
    return terminologies;
  }

  async getPermissions({ projectId }) {
    const permissions = await fetchWithToken(
      `${this.baseUrl}/v1/projects/${projectId}/permissions/current`,
    );
    return permissions;
  }

  async canCreate({ projectId, type = '' }) {
    if (!['pco'].includes(type)) {
      return false;
    }
    const permissions = await this.getValidPermissions({ projectId, type });

    // https://jira.autodesk.com/browse/ACSC-5415
    // here we use a workaround with assumption from cost business logic
    // #1 collaborateCreate is only exposed to limited cost modules and users who have collaborateCreate permission
    // #2 if a user has collaborateCreate permission, it's safe to allow them to create
    return (
      Array.isArray(permissions) &&
      permissions.some(
        ({ resourceType, permissionLevel }) =>
          resourceType.toUpperCase() === type.toUpperCase() &&
          (permissionLevel === 'fullControl' ||
            permissionLevel === 'collaborateCreate'),
      )
    );
  }

  async create(projectId, object, type = '') {
    return fetchWithToken(
      `${this.baseUrl}/v1/projects/${projectId}/${MAPPING_REQUEST_PATH[type]}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(object),
      },
    );
  }

  shouldIgnoreError(error) {
    return error && error.status === 403;
  }
}

export default new CostSdk();
